// src/components/ComponentList/ComponentList.jsx
import React, { useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, ChevronRight, Cpu, Zap, Radio } from 'lucide-react';
import { CATEGORIES } from '../../services/api';
import './ComponentList.css';

const CATEGORY_ICONS = {
  Passive: <Radio size={12} />,
  Semiconductor: <Zap size={12} />,
  IC: <Cpu size={12} />,
  Power: <Zap size={12} />,
  Timing: <Radio size={12} />,
};

const ComponentItem = memo(function ComponentItem({ comp, isSelected, onSelect }) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      className={`comp-item ${isSelected ? 'selected' : ''}`}
      onClick={() => onSelect(comp.id)}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      whileHover={{ x: 4 }}
      whileTap={{ scale: 0.98 }}
      layout
    >
      <div className="comp-dot" style={{ background: comp.color, boxShadow: isSelected ? `0 0 10px ${comp.color}` : 'none' }} />
      <div className="comp-info">
        <div className="comp-name">{comp.name}</div>
        <div className="comp-ref">{comp.ref} · {comp.package}</div>
      </div>
      <div className="comp-value">{comp.value}</div>
      {isSelected && <ChevronRight size={14} className="comp-arrow" style={{ color: comp.color }} />}

      <AnimatePresence>
        {hovered && !isSelected && (
          <motion.div
            className="comp-tooltip"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
          >
            <div className="tooltip-title">{comp.name}</div>
            <div className="tooltip-row"><span>Reference</span><span>{comp.ref}</span></div>
            <div className="tooltip-row"><span>Value</span><span>{comp.value}</span></div>
            <div className="tooltip-row"><span>Package</span><span>{comp.package}</span></div>
            <div className="tooltip-row"><span>Tolerance</span><span>{comp.tolerance}</span></div>
            <div className="tooltip-desc">{comp.description}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
});

const SkeletonItem = () => (
  <div className="comp-item skeleton-item">
    <div className="skeleton" style={{ width: 8, height: 8, borderRadius: '50%' }} />
    <div style={{ flex: 1 }}>
      <div className="skeleton" style={{ width: '60%', height: 12, marginBottom: 6 }} />
      <div className="skeleton" style={{ width: '40%', height: 10 }} />
    </div>
  </div>
);

export default function ComponentList({
  components, loading, selectedId, onSelect,
  searchQuery, setSearchQuery, activeCategory, setActiveCategory,
}) {
  const grouped = CATEGORIES.slice(1).reduce((acc, cat) => {
    const items = components.filter((c) => c.category === cat);
    if (items.length) acc[cat] = items;
    return acc;
  }, {});

  return (
    <div className="comp-list-panel glass">
      <div className="panel-header">
        <span className="panel-title">Components</span>
        {!loading && <span className="comp-count">{components.length}</span>}
      </div>

      <div className="search-bar">
        <Search size={13} strokeWidth={2} />
        <input
          type="text"
          placeholder="Search by name, ref, value…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button className="clear-search" onClick={() => setSearchQuery('')}>
            <X size={12} />
          </button>
        )}
      </div>

      <div className="category-tabs">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            className={`cat-tab ${activeCategory === cat ? 'active' : ''}`}
            onClick={() => setActiveCategory(cat)}
          >
            {CATEGORY_ICONS[cat] || null}
            {cat}
          </button>
        ))}
      </div>

      <div className="comp-scroll">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => <SkeletonItem key={i} />)
        ) : components.length === 0 ? (
          <div className="no-results">No components match</div>
        ) : activeCategory === 'All' ? (
          Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} className="comp-group">
              <div className="group-label">
                {CATEGORY_ICONS[cat]} {cat}
              </div>
              {items
                .filter((c) =>
                  c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  c.ref.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  c.value.toLowerCase().includes(searchQuery.toLowerCase())
                )
                .map((comp) => (
                  <ComponentItem
                    key={comp.id}
                    comp={comp}
                    isSelected={comp.id === selectedId}
                    onSelect={onSelect}
                  />
                ))}
            </div>
          ))
        ) : (
          <AnimatePresence>
            {components.map((comp) => (
              <ComponentItem
                key={comp.id}
                comp={comp}
                isSelected={comp.id === selectedId}
                onSelect={onSelect}
              />
            ))}
          </AnimatePresence>
        )}
      </div>

      {selectedId && (() => {
        const comp = components.find((c) => c.id === selectedId);
        if (!comp) return null;
        return (
          <motion.div
            className="selected-detail"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ borderColor: comp.color + '60' }}
          >
            <div className="detail-title" style={{ color: comp.color }}>{comp.name}</div>
            <div className="detail-desc">{comp.description}</div>
            <div className="detail-grid">
              <div className="detail-cell"><span>REF</span>{comp.ref}</div>
              <div className="detail-cell"><span>VALUE</span>{comp.value}</div>
              <div className="detail-cell"><span>PKG</span>{comp.package}</div>
              <div className="detail-cell"><span>TOL</span>{comp.tolerance}</div>
            </div>
          </motion.div>
        );
      })()}
    </div>
  );
}
